/**
 * NotificationCountItem
 *
 * Covers the four count-based, media-trailing notification types whose subtitle
 * is `"Total: {countText} - {date}"`:
 *   - contentLike   — `notification_content_like_item.dart:42-63`
 *   - contentComment — `notification_content_comment_item.dart:38-60`
 *   - commentLike   — `notification_comment_like_item.dart:38-56`
 *   - commentReply  — `notification_comment_reply_item.dart:38-56`
 *
 * Each varies only by the title verb/object (undo-aware) and which stats count +
 * pluralized noun feeds the "Total:" line, so they share one component varying
 * by l10n key (see the per-type `kind` discriminant). Avatar from
 * `usersInfo.userId`, trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { NotificationCountKind } from '@/domain/enums/inbox/notificationCountKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationCountItemProps extends NotificationItemProps {
  kind: NotificationCountKind;
}

export const NotificationCountItem: React.FC<NotificationCountItemProps> = ({
  kind,
  notification,
  onUserTap,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const n = l10n.inbox.notifications;

  const { info, stats } = notification;
  const mediaInfo = info.mediaInfo;
  const name = info.usersInfo.name;
  const isUndo = info.isUndo === true;
  const isVideo = MediaUtils.mediaInfoIsVideo(mediaInfo);

  // Title (undo-aware verb + object).
  let title: string;
  switch (kind) {
    case NotificationCountKind.contentLike:
      title = isUndo
        ? (isVideo ? n.unlikedYourVideo : n.unlikedYourImage)
        : (isVideo ? n.likedYourVideo : n.likedYourImage);
      break;
    case NotificationCountKind.contentComment:
      title = isUndo
        ? (isVideo ? n.removedCommentOnYourVideo : n.removedCommentOnYourImage)
        : (isVideo ? n.commentedOnYourVideo : n.commentedOnYourImage);
      break;
    case NotificationCountKind.commentLike:
      title = isUndo ? n.unlikedYourComment : n.likedYourComment;
      break;
    case NotificationCountKind.commentReply:
      title = isUndo ? n.removedReplyToYourComment : n.repliedToYourComment;
      break;
  }
  title = title.replace('{name}', name);

  // Count text (zero / one / many pluralization).
  const countText = (() => {
    let count: number;
    let zero: string;
    let one: string;
    let many: string;
    switch (kind) {
      case NotificationCountKind.contentLike:
        count = stats?.contentLikes ?? 0;
        zero = n.zeroLikes; one = n.oneLike; many = n.manyLikes;
        break;
      case NotificationCountKind.contentComment:
        count = stats?.contentComments ?? 0;
        zero = n.zeroComments; one = n.oneComment; many = n.manyComments;
        break;
      case NotificationCountKind.commentLike:
        count = stats?.commentLikes ?? 0;
        zero = n.zeroLikes; one = n.oneLike; many = n.manyLikes;
        break;
      case NotificationCountKind.commentReply:
        count = stats?.commentReplies ?? 0;
        zero = n.zeroReplies; one = n.oneReply; many = n.manyReplies;
        break;
    }
    if (count === 0) return zero;
    if (count === 1) return one;
    return many.replace('{count}', MathUtils.getReactionsIntlNumber(count));
  })();

  const subtitle = n.totalSubtitle
    .replace('{value}', countText)
    .replace('{date}', formatDate(info.timestamp));

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={subtitle}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
